import React, { useState, useEffect, useCallback } from "react";
import { FaArrowRight, FaArrowLeft, FaTimes, FaUserShield, FaFolder, FaClipboard, FaUserFriends, FaBriefcase, FaUserTie } from "react-icons/fa";
import axiosInstance from "../../../services/AxiosInstance";
import "./TutorialModal.css";

import createCustomerImg from "../../../images/tutorial/create-customer.png";
import createCategoryImg from "../../../images/tutorial/create-category.png";
import createTaskImg from "../../../images/tutorial/create-task.png";
import createEmployeeImg from "../../../images/tutorial/create-employee.png";
import createJobImg from "../../../images/tutorial/create-job.png";
import adminManagementImg from "../../../images/tutorial/admin-management.png";

const tutorialSteps = [
    {
        id: 1,
        icon: <FaUserShield className="w-5 h-5" />,
        title: "Add Your Customers",
        subtitle: "Step 1 — Customer Management",
        image: createCustomerImg,
        description:
            "Start by adding the customers your organisation serves. Enter their basic information like name, email, and phone number along with their address details.",
        highlights: [
            "Add customer name, email & phone number",
            "Enter address details including street and city",
            "View and manage all customers from the Customer List",
        ],
        tip: "Navigate to Customer → Add Customer from the sidebar to get started.",
    },
    {
        id: 2,
        icon: <FaFolder className="w-5 h-5" />,
        title: "Create Job Categories",
        subtitle: "Step 2 — Organise Your Work",
        image: createCategoryImg,
        description:
            "Job categories help you organise different types of work your team handles. Create categories to classify and group your jobs efficiently.",
        highlights: [
            "Define category types to classify your jobs",
            "Organise jobs under meaningful groups",
            "Manage all categories from the Category List",
        ],
        tip: "Go to Job Categories → Create Job Category to add a new category.",
    },
    {
        id: 3,
        icon: <FaClipboard className="w-5 h-5" />,
        title: "Create Tasks",
        subtitle: "Step 3 — Define Actionable Tasks",
        image: createTaskImg,
        description:
            "Tasks are individual work items that can be assigned within jobs. Define task details including the title, cost, estimated time, category, and a description.",
        highlights: [
            "Set task title, cost & estimated time range",
            "Assign each task to a specific category",
            "Add detailed descriptions (up to 100 words)",
        ],
        tip: "Head to Tasks → Create Tasks to define your first task.",
    },
    {
        id: 4,
        icon: <FaUserFriends className="w-5 h-5" />,
        title: "Add Employees",
        subtitle: "Step 4 — Build Your Team",
        image: createEmployeeImg,
        description:
            "Add your team members by providing their name and email. The email will be validated to ensure it's unique and properly formatted before the employee is added.",
        highlights: [
            "Enter employee name and email address",
            "Email is validated before creating the account",
            "View and manage all employees from the Employee List",
        ],
        tip: "Navigate to Team → Add Employee to onboard your team members.",
    },
    {
        id: 5,
        icon: <FaBriefcase className="w-5 h-5" />,
        title: "Create Jobs",
        subtitle: "Step 5 — Assign Work to Your Team",
        image: createJobImg,
        description:
            "Jobs bring everything together. Assign a job title, select a category, choose a customer, and assign employees. Set the date, time, and recurrence to schedule work efficiently.",
        highlights: [
            "Set job title, category, customer & employee",
            "Schedule date, start time & end time",
            "Configure recurrence and cost visibility options",
        ],
        tip: "Go to Jobs → Create Job to create and schedule your first job.",
    },
    {
        id: 6,
        icon: <FaUserTie className="w-5 h-5" />,
        title: "Manage Admins",
        subtitle: "Step 6 — Control Access",
        image: adminManagementImg,
        description:
            "Add and manage administrators for your organisation. Admins can help oversee operations, manage team members, and handle day-to-day tasks within the platform.",
        highlights: [
            "Add new admins to share management responsibilities",
            "View admin status and contact information",
            "Control who has administrative access",
        ],
        tip: "Navigate to Team → Manage Admins to add or review admin users.",
    },
];

const TutorialModal = ({ isOpen, onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);

    const totalSteps = tutorialSteps.length;
    const step = tutorialSteps[currentStep];
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    const completeOnboarding = useCallback(async () => {
        setIsCompleting(true);
        try {
            await axiosInstance.patch("/v1/user/complete-onboarding");
            // Update localStorage so tutorial won't show again
            const userDetails = JSON.parse(localStorage.getItem("userDetails"));
            if (userDetails && userDetails.user) {
                userDetails.user.isOnboardingCompleted = true;
                localStorage.setItem("userDetails", JSON.stringify(userDetails));
            }
            localStorage.removeItem("isFirstLogin");
        } catch (error) {
            console.error("Error completing onboarding:", error);
        } finally {
            setIsCompleting(false);
            onClose();
        }
    }, [onClose]);

    // Reset step when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
        }
    }, [isOpen]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape") handleSkip();
            if (e.key === "ArrowRight" && !isLastStep) goToNext();
            if (e.key === "ArrowLeft" && !isFirstStep) goToPrev();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, currentStep]);

    const goToNext = useCallback(() => {
        if (isLastStep) return;
        setCurrentStep((prev) => prev + 1);
    }, [isLastStep]);

    const goToPrev = useCallback(() => {
        if (isFirstStep) return;
        setCurrentStep((prev) => prev - 1);
    }, [isFirstStep]);

    const goToStep = useCallback((index) => {
        if (index === currentStep) return;
        setCurrentStep(index);
    }, [currentStep]);

    const handleSkip = useCallback(() => {
        localStorage.removeItem("isFirstLogin");
        onClose();
    }, [onClose]);

    if (!isOpen) return null;

    return (
        <div className="tutorial-overlay" onClick={handleSkip}>
            <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
                {/* Skip / Close button */}
                <button className="tutorial-skip-btn" onClick={handleSkip} title="Skip Tutorial">
                    <FaTimes className="w-3.5 h-3.5" />
                    <span>Skip</span>
                </button>

                {/* Top progress bar */}
                <div className="tutorial-progress-bar">
                    <div
                        className="tutorial-progress-fill"
                        style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                    />
                </div>

                {/* Step counter badge */}
                <div className="tutorial-step-badge">
                    <span className="tutorial-step-badge-icon">{step.icon}</span>
                    <span>{step.subtitle}</span>
                </div>

                {/* Main content area */}
                <div className="tutorial-content">
                    {/* Title */}
                    <h2 className="tutorial-title">{step.title}</h2>

                    {/* Image */}
                    <div className="tutorial-image-container">
                        <img
                            src={step.image}
                            alt={step.title}
                            className="tutorial-image"
                        />
                    </div>

                    {/* Description */}
                    <p className="tutorial-description">{step.description}</p>

                    {/* Highlights */}
                    <div className="tutorial-highlights">
                        {step.highlights.map((highlight, idx) => (
                            <div key={idx} className="tutorial-highlight-item">
                                <div className="tutorial-highlight-dot" />
                                <span>{highlight}</span>
                            </div>
                        ))}
                    </div>

                    {/* Tip */}
                    <div className="tutorial-tip">
                        <span className="tutorial-tip-label">💡 Quick Tip</span>
                        <span>{step.tip}</span>
                    </div>
                </div>

                {/* Footer with navigation */}
                <div className="tutorial-footer">
                    {/* Dot indicators */}
                    <div className="tutorial-dots">
                        {tutorialSteps.map((_, idx) => (
                            <button
                                key={idx}
                                className={`tutorial-dot ${idx === currentStep ? "tutorial-dot-active" : ""} ${idx < currentStep ? "tutorial-dot-completed" : ""}`}
                                onClick={() => goToStep(idx)}
                                title={`Step ${idx + 1}`}
                            />
                        ))}
                    </div>

                    {/* Navigation buttons */}
                    <div className="tutorial-nav-buttons">
                        <button
                            className={`tutorial-btn tutorial-btn-prev ${isFirstStep ? "tutorial-btn-disabled" : ""}`}
                            onClick={goToPrev}
                            disabled={isFirstStep}
                        >
                            <FaArrowLeft className="w-3.5 h-3.5" />
                            <span>Previous</span>
                        </button>

                        {isLastStep ? (
                            <button className="tutorial-btn tutorial-btn-finish" onClick={completeOnboarding} disabled={isCompleting}>
                                <span>{isCompleting ? "Finishing..." : "Get Started"}</span>
                                <FaArrowRight className="w-3.5 h-3.5" />
                            </button>
                        ) : (
                            <button className="tutorial-btn tutorial-btn-next" onClick={goToNext}>
                                <span>Next</span>
                                <FaArrowRight className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TutorialModal;
